import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { automationImages } from '@images/automation-images';
import { commonImages } from '@images/common-images';
import { Pagination } from '@models/pagination.model';
import { ApiService } from '@services/api/api.service';
import { WorkflowService } from '@services/api/workflow.service';
import { Utils } from '@services/helpers/utils';
import { LeadManagementService } from '@services/lm-service/lead-management.service';
import { ToastrService } from 'ngx-toastr';
import { Paginator } from 'primeng/paginator';

@Component({
  selector: 'app-centralized-module-processes',
  templateUrl: './centralized-module-processes.component.html',
  styleUrls: ['./centralized-module-processes.component.scss'],
})
export class CentralizedModuleProcessesComponent implements OnInit, OnDestroy {
  pagination: Pagination;
  search_keyword: string = '';
  images = automationImages;
  common_images = commonImages;
  processHistoryList: any[] = [];
  Utils = Utils; // 👈 expose Utils class to the template
  activatedHistoryName: string = '';
  activeTabName: 'progress' | 'paused' | 'completed' = 'progress';
  @ViewChild('paginator', { static: true }) paginator: Paginator;

  constructor(
    public _workflowService: WorkflowService,
    public lmService: LeadManagementService,
    private toast: ToastrService,
    private api: ApiService,
    private remmiConfirm: RemmiConfirmService
  ) {}

  ngOnInit(): void {
    // Get the rows from the paginatorState
    const { paginatorState } = this.paginator ? this.paginator : { paginatorState: [] };
    const { rows } = paginatorState;
    // Set the first record index to 0
    this.first = 0;
    // Reset the pagination
    this._workflowService.centralizedProcessPagination = {
      ...this._workflowService.centralizedProcessPagination,
      per_page: 10,
      total_pages: 0,
      total: 0,
      numberOfRecords: rows || 0,
    };
    this.pagination = { ...this.pagination, per_page: 10, total_pages: 0, total: 0, numberOfRecords: rows || 0 };
    // Clear the process history list
    this.processHistoryList = [];
  }

  ngOnDestroy(): void {
    this._workflowService.moduleName = '';
  }

  GetProcessesStatus(processes: any) {
    // Return the status of the process if it exists in the list
    // First, search for the record id in the list
    // If not found, then search for 'all' in the list
    // If not found, then return an empty string
    if (processes) {
      var status = processes?.filter(
        res => res._id === this._workflowService.processRecordId || res.id === this._workflowService.processRecordId
      )?.[0]?.process;
      if (!status) {
        status = processes?.filter(res => res._id === 'all' || res.id === 'all')?.[0]?.process;
      }
      return status;
    }
    return '';
  }

  ChangeProcessStatus(trigger: any) {
    // Confirm before pausing if the process is not completed
    if (trigger?.process_completed == false) {
      this.remmiConfirm
        .ask({
          title: 'Pause Running Processes?',
          message: 'Processes are currently running. Do you want to pause them?',
          subject: trigger?.tigger_title,
          confirmLabel: 'Pause',
          tone: 'warning',
        })
        .subscribe(isConfirmed => isConfirmed && this.ChangeStatus(trigger));
    } else {
      let filteredProcess: any[] = trigger?.filter;
      let isExists: boolean = false;
      if (filteredProcess) {
        var status = filteredProcess?.filter(
          res => res._id === this._workflowService.processRecordId || res.id === this._workflowService.processRecordId
        )?.[0]?.process;
        if (!status) {
          status = filteredProcess?.filter(res => res._id === 'all' || res.id === 'all')?.[0]?.process;
        } else {
          isExists = true;
        }
        status = status == 'play' ? 'stop' : 'play';
        if (isExists) {
          filteredProcess.filter(
            res => res._id === this._workflowService.processRecordId || res.id === this._workflowService.processRecordId
          )[0].process = status;
        } else {
          filteredProcess.push({ _id: this._workflowService.processRecordId, process: status });
        }
        // Update the trigger with the new status
        this._workflowService.UpdateTriggerById(trigger?._id, { ...trigger, filter: filteredProcess }).subscribe({
          next: response => {
            if (response.status) {
              this.GetProcessByModules();
            }
          },
        });
      }
    }
  }
  ChangeStatus(trigger: any) {
    // Change the status of the trigger to play or pause
    let filteredProcess: any[] = trigger?.filter;
    let isExists: boolean = false;
    if (filteredProcess) {
      // Check if the process record id is present in the filter array
      var status = filteredProcess?.filter(
        res => res._id === this._workflowService.processRecordId || res.id === this._workflowService.processRecordId
      )?.[0]?.process;
      if (!status) {
        // If not, check if there is an 'all' record in the filter array
        status = filteredProcess?.filter(res => res._id === 'all' || res.id === 'all')?.[0]?.process;
      } else {
        // If present, then change the status
        isExists = true;
      }
      // Toggle the status between play and pause
      status = status == 'play' ? 'stop' : 'play';
      if (isExists) {
        // Update the existing record
        filteredProcess.filter(
          res => res._id === this._workflowService.processRecordId || res.id === this._workflowService.processRecordId
        )[0].process = status;
      } else {
        // Add a new record to the filter array
        filteredProcess.push({ _id: this._workflowService.processRecordId, process: status });
      }
      // Update the trigger
      this._workflowService.UpdateTriggerById(trigger?._id, { ...trigger, filter: filteredProcess }).subscribe({
        next: response => {
          if (response.status) {
            // Refresh the process list
            this.GetProcessByModules();
          }
        },
      });
    }
  }
  debounceTimeout: any;
  FilterSearchProcessList(value: any = null) {
    // Debounce the search input to avoid excessive API calls
    clearTimeout(this.debounceTimeout);
    this.debounceTimeout = setTimeout(() => {
      this.GetProcessByModules(1, this._workflowService.centralizedProcessPagination.per_page, value);
    }, 0);
  }
  GetProcessByModules(pageNumber: number = 1, pageSize: number = 10, keyword: string = '', bool = false) {
    this._workflowService.ActiveProcessList = [];
    let moduleName = this._workflowService.moduleName;
    if (!moduleName) {
      return;
    }
    // fetch the workflow processes for the selected record
    this._workflowService
      .GetAllProcessesByModule(this._workflowService.processRecordId, moduleName, pageNumber, pageSize, keyword, this.activeTabName)
      .subscribe({
        next: response => {
          try {
            const { status, data, meta } = response;
            if (status) {
              this.isLoading = true;
              let activeProcessList = [];
              this.activeTabName === 'progress' && data?.length
                ? (this._workflowService.module_isStop = false)
                : (this._workflowService.module_isStop = true);
              this._workflowService.isLoad = false;
              activeProcessList = data?.map(
                result => (result = { ...result, module: Utils.capitalizePhrase(result?.module?.split('_')?.join(' ')?.trim() || '') })
              );
              this._workflowService.centralizedProcessPagination = {
                ...this._workflowService.centralizedProcessPagination,
                per_page: 10,
                total_pages: Math.ceil(meta?.pagination?.total / 10),
                total: meta?.pagination?.total,
                numberOfRecords: this._workflowService.ActiveProcessList?.length > 10 ? 10 : this._workflowService.ActiveProcessList?.length,
              };

              if (bool) {
                this._workflowService.ActiveProcessList.push(...activeProcessList);
                if (activeProcessList.length < 9) {
                  this.isLoading = false;
                  this.rows = this._workflowService.ActiveProcessList.length;
                } else {
                  this.rows += 10;
                }
              } else {
                this.page = 1;
                this.rows = 10;
                this._workflowService.ActiveProcessList = activeProcessList;
              }
            } else {
              this._workflowService.ActiveProcessList = [];
            }
          } catch (error) {
            this._workflowService.ActiveProcessList = [];
          }
        },
      });
  }

  changeStatus() {
    // Toggle the workflow process for the selected record
    let data = {
      office_id: this.api.getOfficeId(),
      module: this._workflowService.active_module_name,
      record_id: this._workflowService.processRecordId,
      is_stop: this._workflowService.module_isStop == true ? true : false,
    };
    this.api.post('/all-workflow-process-stop', data).subscribe({
      next: _ => {
        this.toast.success(`Process ${this._workflowService.module_isStop == true ? 'pause' : 'start'} successfully`);
        this.GetProcessByModules();
      },
    });
  }

  processPagination: Pagination;
  first: number = 0;
  onChangePageNumber(event) {
    const { first, rows, page } = event;
    // Update the pagination details and fetch the next page of workflow processes
    this._workflowService.centralizedProcessPagination = { ...this._workflowService.centralizedProcessPagination, numberOfRecords: rows || 0 };
    this.first = first;
    this.GetProcessByModules(page + 1, rows);
  }

  @Output() triggerEmitter: EventEmitter<any> = new EventEmitter<any>();
  showProcessWorkflow(process) {
    this._workflowService.isWorkflowProcessTabOpened = false;
    this.lmService.selected_workflow = null; // make empty if data exits *DONT REMOVE*
    setTimeout(() => {
      if (process?.parent == -1) {
        // If parent is -1 then it's a main workflow process
        this.lmService.selected_workflow = process;
        setTimeout(() => {
          let tabElement = document.getElementById('pills-autoprocs-tab');
          tabElement ? tabElement?.click() : this.lmService.addTabInLevels('Workflow', process._id);
        }, 500);
      } else {
        // If parent is not -1 then it's a sub workflow process
        this.api.get(`/workflow/get-by-id/${process._id}`).subscribe({
          next: response => {
            if (response.status) {
              let ele = response.data;
              this.lmService.selected_workflow = ele;
              setTimeout(() => {
                let tabElement = document.getElementById('pills-autoprocs-tab');
                tabElement ? tabElement?.click() : this.lmService.addTabInLevels('Workflow', process._id);
              }, 500);
            }
          },
        });
      }
    }, 2000);
  }

  ViewProcessHistory(processid: any, pageNumber = 1, pageSize = 10, bool = false) {
    // Fetch the workflow history and show the workflow
    // If the bool is true then it will append the data to the existing array
    // else it will make the array empty and add the new data
    this.activatedHistoryName = '';
    this._workflowService.GetAllHistoryProcesses(processid, this._workflowService.processRecordId, pageNumber, pageSize).subscribe({
      next: response => {
        try {
          const { status, data, meta } = response;
          if (status) {
            let processHistory = [];
            this.process_loading = true;
            processHistory = data;
            this.activatedHistoryName = this._workflowService.ActiveProcessList?.find((result: any) => result?._id === processid)?.tigger_title || '';
            this.pagination = { ...this.pagination, ...meta?.pagination };
            if (bool) {
              this.processHistoryList.push(...processHistory);
              if (processHistory.length < 9) {
                this.process_loading = false;
                this.process_rows = this.processHistoryList?.length;
              } else {
                this.process_rows += 10;
              }
            } else {
              this.process_page = 1;
              this.process_rows = 10;
              this.processHistoryList = processHistory;
            }
          } else {
            this.toast.error('No record found');
            this.processHistoryList = [];
          }
        } catch (error) {
          this.processHistoryList = [];
        }
      },
    });
  }

  LoadWorkflowHandler(process_history: any) {
    // Remove the tab level for the workflow
    this.RemoveTabLevel(process_history?.workflow?.[0]?._id);
    // Fetch the workflow history detail and show the workflow
    this._workflowService.GetWorkflowHistoryDetailById(process_history?._id).subscribe({
      next: response => {
        const { status, data } = response;
        if (status && data && Object.keys(data)?.length > 0) {
          this.showProcessWorkflow({ ...data?.workflow?.[0], is_readonly: true, children: data?.children });
          // Close the workflow tab
          this._workflowService.isWorkflowProcessTabOpened = false;
        } else {
          this.toast.error('Something went wrong');
        }
      },
      error: _ => {
        this.toast.error('Something went wrong');
      },
    });
  }

  RemoveTabLevel(id) {
    // Remove the workflow tab level if it exists
    let workflow_index: number = this.lmService.tab_levels.findIndex(el => el?.name == 'Workflow' && el?._id === id);
    if (workflow_index > -1) {
      // Remove the tab level from the array
      this.lmService.tab_levels.splice(workflow_index, 1);
      // Update the active tab index to the last tab level in the array
      this.lmService.activeTabIndex = this.lmService.tab_levels?.length - 1;
    }
  }

  onWorkflowChangeTab(workflow_type: 'progress' | 'paused' | 'completed' = 'progress') {
    // Update the active tab name and fetch processes for the selected tab
    this.activeTabName = workflow_type;
    this.GetProcessByModules();
  }

  // ------------- ActiveProcessList Pagination ----------------

  rows: number = 10;
  page: number = 1;
  isLoading: boolean = true;
  scrollTop: number = 0;
  @ViewChild('scrollableElement') scrollableElement!: ElementRef;
  scrollToTop(): void {
    // Scroll the ActiveProcessList to the top
    const element = this.scrollableElement.nativeElement as HTMLElement;
    element.scrollTop = 0;
  }
  onScroll(event: Event): void {
    const element = event.target;
    this.scrollTop = element['scrollTop'];
    let a: any = element['scrollHeight'] - element['scrollTop'];
    let b: any = element['scrollHeight'] - element['scrollTop'];
    let c = element['clientHeight'];

    // Check if the user has scrolled to the bottom of the div
    // and if the loading variable is true
    // then increase the page number and call the GetProcessByModules with the new page number
    if ((parseInt(a) - 5 < parseInt(c) || parseInt(b) == parseInt(c)) && this.isLoading) {
      this.page = this.page + 1;
      this.isLoading = false;
      this.GetProcessByModules(this.page, 10, '', true);
    }
  }

  // ------------- ProcessHistoryList Pagination ----------------

  process_rows: number = 10;
  process_page: number = 1;
  process_loading: boolean = true;
  process_scrollTop: number = 0;
  @ViewChild('process_ScrollableElement') process_ScrollableElement!: ElementRef;
  process_ScrollToTop(): void {
    // Scroll the process history list to the top
    const element = this.process_ScrollableElement.nativeElement as HTMLElement;
    element.scrollTop = 0;
  }
  onProcess_Scroll(event: Event): void {
    const element = event.target;
    this.process_scrollTop = element['scrollTop'];
    let a: any = element['scrollHeight'] - element['scrollTop'];
    let b: any = element['scrollHeight'] - element['scrollTop'];
    let c = element['clientHeight'];

    // Check if the user has scrolled to the bottom of the div
    // and if the loading variable is true
    // then increase the page number and call the ViewProcessHistory with the new page number
    if ((parseInt(a) - 5 < parseInt(c) || parseInt(b) == parseInt(c)) && this.process_loading) {
      this.process_page = this.process_page + 1;
      this.isLoading = false;
      this.ViewProcessHistory(this.processHistoryList?.[0]?.workflow_id, this.process_page, 10, true);
    }
  }
}
